import virtualTerminalStore from "../../../store/VirtualTerminalStore";
import {
  applePayAdapter,
  getMode,
  isWalletPaymentSuccess,
  postExpressCheckoutEvent,
  processWalletPayment,
  walletErrorFromResult,
} from "..";
import { buildApplePayBridgeEvent } from "../events";
import { fetchBounded, runWithToken } from "../token-queue";

// ─── API versions ──────────────────────────────────────────────────────────
//
// Apple Pay JS v14 is required for merchant-token request types (macOS 13 /
// iOS 16+): `recurringPaymentRequest` (autopay) and `deferredPaymentRequest`
// (tokenization, so Apple issues a reusable MPAN instead of a single-use DPAN).
// v3 is the one-time fallback so older Safari keeps working.
export const APPLE_PAY_RECURRING_API_VERSION = 14;
export const APPLE_PAY_ONE_TIME_API_VERSION = 3;

// Sheet-only placeholder for tokenization's required `deferredPaymentDate`.
// This is NOT a token TTL — the MPAN remains valid after this date.
export const APPLE_PAY_TOKENIZATION_DEFERRED_DAYS = 30;

// Upper bound for the host to respond to `start` (merchant validation fires
// almost immediately once the host's session begins). If nothing comes back the
// host is missing/old (e.g. a merchant running an outdated component.js), so we
// surface an error instead of leaving the button silently dead.
export const APPLE_PAY_HOST_HANDSHAKE_TIMEOUT_MS = 15000;

// Resolves the Apple Pay JS API version required by the configured flow.
export function resolveApiVersion(payabliConfig) {
  const mode = getMode(payabliConfig);
  return mode === "autopay" || mode === "tokenization"
    ? APPLE_PAY_RECURRING_API_VERSION
    : APPLE_PAY_ONE_TIME_API_VERSION;
}

// ─── Recurring disclosure ────────────────────────────────────────────────────

// Map our public `autopay.frequency` values onto Apple's
// `recurringPaymentIntervalUnit` + `recurringPaymentIntervalCount`.
// Apple only supports year/month/day/hour/minute, so weeks are expressed as days.
// `onetime` is intentionally absent — it means "no recurring cycle", so the
// sheet should not advertise a recurring disclosure.
const FREQUENCY_TO_APPLE_INTERVAL = {
  weekly: { unit: "day", count: 7 },
  every2weeks: { unit: "day", count: 14 },
  monthly: { unit: "month", count: 1 },
  every3months: { unit: "month", count: 3 },
  every6months: { unit: "month", count: 6 },
  annually: { unit: "year", count: 1 },
};

function parseDate(value) {
  if (!value) return undefined;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

function formatAmount(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
}

function managementURLFor(appUrl, entryPoint) {
  return `${appUrl}/${entryPoint}/signin`;
}

// Sheet placeholder for tokenization. Apple requires `deferredPaymentDate`;
// the date is disclosure-only and does not expire the issued MPAN.
function tokenizationDeferredPaymentDate(now = new Date()) {
  const date = new Date(now.getTime());
  date.setDate(date.getDate() + APPLE_PAY_TOKENIZATION_DEFERRED_DAYS);
  return date;
}

// Builds Apple's `ApplePayDeferredPaymentRequest` for card-on-file tokenization.
// Apple issues an MPAN for deferred/recurring/reload requests; deferred is the
// semantically correct type for "save card to charge later" and avoids showing
// a fake monthly subscription on the sheet.
//
// Returns `undefined` unless mode is "tokenization".
export function buildApplePayDeferredRequest({
  expressCheckout,
  entryPoint,
  appUrl,
} = {}) {
  if (expressCheckout?.mode !== "tokenization") return undefined;

  const authAmount = formatAmount(expressCheckout?.fallbackAuthAmount ?? 0.01);
  const label = expressCheckout.label || "Save payment method";
  return {
    paymentDescription: label,
    deferredBilling: {
      label,
      amount: authAmount,
      paymentTiming: "deferred",
      deferredPaymentDate: tokenizationDeferredPaymentDate(),
    },
    managementURL: managementURLFor(appUrl, entryPoint),
  };
}

// Builds Apple's `ApplePayRecurringPaymentRequest` from our public autopay
// config: cycle, dates, and management URL for the recurring sheet disclosure.
//
// Returns `undefined` for non-autopay flows, or for autopay with a frequency
// that does not map to an Apple cycle (e.g. `onetime`).
export function buildApplePayRecurringRequest({
  expressCheckout,
  entryPoint,
  appUrl,
} = {}) {
  if (expressCheckout?.mode !== "autopay") return undefined;

  const managementURL = managementURLFor(appUrl, entryPoint);

  const autopay = expressCheckout?.autopay;
  const interval = FREQUENCY_TO_APPLE_INTERVAL[autopay?.frequency];
  if (!interval) return undefined;

  const totalAmount = formatAmount(
    Number(expressCheckout.amount) + Number(expressCheckout.fee)
  );
  const label = expressCheckout.label || "Subscription";

  // Note: Apple's `paymentTiming: "recurring"` is dictated by the Apple Pay JS
  // SDK and is NOT related to our partner-facing `autopay` name.
  const regularBilling = {
    label,
    amount: totalAmount,
    paymentTiming: "recurring",
    recurringPaymentIntervalUnit: interval.unit,
    recurringPaymentIntervalCount: interval.count,
  };

  const startDate = parseDate(autopay?.startDate);
  if (startDate) regularBilling.recurringPaymentStartDate = startDate;

  const endDate = parseDate(autopay?.endDate);
  if (endDate) regularBilling.recurringPaymentEndDate = endDate;

  return {
    paymentDescription: label,
    regularBilling,
    managementURL,
  };
}

// ─── Sheet request ───────────────────────────────────────────────────────────

// Builds the `ApplePayPaymentRequest` consumed by `ApplePaySession`. Per-mode:
//   - one_time     → total = amount + fee, Subtotal + Service Fee line items.
//   - autopay      → same totals, plus a recurring disclosure (see above).
//   - tokenization → $0.01 authorization, single Subtotal line, plus a deferred
//                    disclosure so Apple issues a reusable MPAN without showing
//                    a fake monthly subscription.
//
// The Payabli charge payload (getpaid / Subscription) is built separately by
// `processWalletPayment`, keeping the wallet-agnostic logic shared with Google Pay.
export function buildApplePaySessionRequest(payabliConfig = {}) {
  const { expressCheckout = {} } = payabliConfig;
  const isTokenization = getMode(payabliConfig) === "tokenization";

  const subtotal = isTokenization
    ? "0.01"
    : formatAmount(expressCheckout.amount);
  const fee = formatAmount(expressCheckout.fee);
  const total = isTokenization
    ? "0.01"
    : formatAmount(Number(expressCheckout.amount) + Number(expressCheckout.fee));

  const request = {
    countryCode: "US",
    currencyCode: expressCheckout.currency || "USD",
    merchantCapabilities: ["supports3DS"],
    supportedNetworks: expressCheckout.supportedNetworks || [],
    requiredBillingContactFields: ["postalAddress", "name"],
    requiredShippingContactFields: expressCheckout?.requiredShippingContactFields
      ? ["postalAddress", "name", "phoneticName", "phone", "email"]
      : [],
    lineItems: [
      { label: "Subtotal", amount: subtotal },
      ...(isTokenization ? [] : [{ label: "Service Fee", amount: fee }]),
    ],
    total: {
      label: expressCheckout.label || "Confirm Payment",
      type: "final",
      amount: total,
    },
  };

  const merchantTokenArgs = {
    expressCheckout,
    entryPoint: payabliConfig.entryPoint,
    appUrl: process.env.REACT_APP_APP_URL,
  };
  const deferredPaymentRequest = buildApplePayDeferredRequest(merchantTokenArgs);
  if (deferredPaymentRequest) {
    request.deferredPaymentRequest = deferredPaymentRequest;
  }
  const recurringPaymentRequest = buildApplePayRecurringRequest(merchantTokenArgs);
  if (recurringPaymentRequest) {
    request.recurringPaymentRequest = recurringPaymentRequest;
  }

  return request;
}

// ─── Browser routing ─────────────────────────────────────────────────────────

// Safari exposes `window.ApplePaySession` natively; third-party browsers
// (Chrome/Edge/Firefox) only get it once `apple-pay-sdk.js` loads. ExpressCheckout
// records whether it existed BEFORE loading the SDK (`__payabliHadNativeApplePaySession`),
// which lets us tell them apart:
//   - Safari       → run the sheet in this iframe; its OS-rendered sheet escapes.
//   - third-party  → run ApplePaySession at the top level (host), because the SDK
//                    draws the QR into the DOM and it would otherwise be trapped
//                    inside this iframe.
export function isThirdPartyApplePay() {
  return !!window.ApplePaySession && !window.__payabliHadNativeApplePaySession;
}

// Best-effort resolution of the top-level (merchant) origin, used to scope the
// Apple Pay bridge postMessage traffic — the only flow carrying a payment
// credential across the iframe boundary. Falls back to "*" (no restriction) when
// the origin can't be determined, so the bridge degrades to the existing
// behavior rather than breaking.
export function resolveParentOrigin() {
  try {
    const ancestors = window.location.ancestorOrigins;
    if (ancestors && ancestors.length) return ancestors[0];
  } catch (_) {
    // ancestorOrigins unsupported (e.g. Firefox); fall through.
  }
  try {
    if (document.referrer) return new URL(document.referrer).origin;
  } catch (_) {
    // Malformed/empty referrer.
  }
  return "*";
}

// Upper bound for the top-level capability probe. The host replies as soon as
// its (memoized) SDK load settles — usually already done by the time the iframe
// reaches its availability check — so this is a safety net for a merchant on an
// old component.js with no capability handler. On timeout we assume unavailable
// so we never mount a third-party button that can't complete.
export const APPLE_PAY_CAPABILITY_TIMEOUT_MS = 6000;

// Asks the host whether the TOP-LEVEL page can actually run an Apple Pay session
// (SDK present, not blocked by the merchant's CSP). Third-party browsers delegate
// the session to the host, so a button that can't be serviced there would only
// fail on click — we gate mounting on this. Resolves false on timeout / no host.
export function requestTopLevelApplePayAvailability(
  randomId,
  timeoutMs = APPLE_PAY_CAPABILITY_TIMEOUT_MS
) {
  return new Promise((resolve) => {
    const parentOrigin = resolveParentOrigin();
    let timer = null;
    const done = (available) => {
      if (timer) clearTimeout(timer);
      timer = null;
      window.removeEventListener("message", onMessage);
      resolve(available);
    };
    const onMessage = (event) => {
      // Same defense as the payment bridge: only trust the top-level parent.
      if (event.source && event.source !== window.parent) return;
      if (parentOrigin !== "*" && event.origin !== parentOrigin) return;
      if (
        event?.data?.event ===
        buildApplePayBridgeEvent("capability-result", randomId)
      ) {
        done(!!(event.data.data && event.data.data.available));
      }
    };
    window.addEventListener("message", onMessage);
    window.parent.postMessage(
      { event: buildApplePayBridgeEvent("capability-query", randomId), data: {} },
      parentOrigin
    );
    timer = setTimeout(() => done(false), timeoutMs);
  });
}

// ─── Error feedback ──────────────────────────────────────────────────────────

// Apple Pay sandbox is signalled by the merchant identifier (Payabli's sandbox
// merchant IDs contain "sandbox"). Used to surface a developer-actionable hint
// when the sheet is dismissed without authorizing — in sandbox that is almost
// always a tester / test-card misconfiguration, which Apple does NOT expose as
// an error (`oncancel` carries no usable reason).
export function isSandboxApplePay(payabliConfig) {
  return /sandbox/i.test(payabliConfig?.applePayMerchantId || "");
}

export const APPLE_PAY_SANDBOX_HINT =
  "Apple Pay could not be completed. In the Apple Pay sandbox this usually means " +
  "the device is signed into a real Apple Account or has a real card in Wallet — " +
  "sign in with an Apple Pay Sandbox Tester account and add a sandbox test card.";

// A valid Apple merchant session always carries a `merchantSessionIdentifier`.
// When validation fails, the backend / Apple gateway returns an error object
// instead. We turn that into a real Error so it reaches the partner's error
// callback, rather than silently failing inside `completeMerchantValidation`
// (which only yields a reasonless "Payment Not Completed" → cancel).
export function merchantSessionError(session) {
  if (session && session.merchantSessionIdentifier) return undefined;
  const message =
    session?.statusMessage ||
    session?.statusMessageDescription ||
    session?.message ||
    (typeof session?.error === "string" ? session.error : undefined);
  return new Error(message || "Apple Pay merchant validation failed");
}

// ─── Orchestrator ────────────────────────────────────────────────────────────

export class ApplePay {
  constructor(payabliConfig, paypoint) {
    this.urlApi = process.env.REACT_APP_URL_API;
    this.payabliConfig = payabliConfig;
    this.paypoint = paypoint;
    this.mode = getMode(payabliConfig);
    this.apiVersion = resolveApiVersion(payabliConfig);
    // The payload the Apple Pay sheet itself consumes.
    this.request = buildApplePaySessionRequest(payabliConfig);
  }

  // Fetches an Apple Pay merchant session from the Payabli backend, used by
  // `onvalidatemerchant`. `domain` must be the top-level (parent) domain
  // registered with Apple, which the backend uses as the `initiativeContext`.
  //
  // Temporary tokens are single-use. Each call reads the current token at call
  // time, replaces it with the `pageIdentifier` the backend adds to the response
  // body, and returns the session WITHOUT that field (Apple and the host bridge
  // must never see it). Calls are serialized across instances so a double click
  // or a repeated validation can never send the same token twice. A valid
  // session that carries no `pageIdentifier` means the token is spent and the
  // payment that follows would 401, so the attempt fails closed.
  fetchMerchantSession(domain, validationURL) {
    return runWithToken(() =>
      this.requestMerchantSession(domain, validationURL)
    );
  }

  async requestMerchantSession(domain, validationURL) {
    const token = virtualTerminalStore.token;
    if (!token) throw new Error("Apple Pay merchant validation failed");
    let body;
    try {
      ({ body } = await fetchBounded(
        `${this.urlApi}Wallet/applepay/session`,
        {
          method: "POST",
          body: JSON.stringify({
            entry: this.payabliConfig.entryPoint,
            domainName: domain,
            displayName: "",
            validationURL,
          }),
          headers: {
            "Content-Type": "application/json",
            requestToken: token,
          },
        },
        (response) => response.json()
      ));
    } catch (error) {
      // The token may have been spent without us learning the rotated one.
      // Drop it so a retry fails closed instead of sending a spent token.
      virtualTerminalStore.setToken("");
      throw error;
    }
    if (!body || typeof body !== "object") {
      virtualTerminalStore.setToken("");
      return body;
    }

    // Error responses issued after auth rotate too, so a retry stays valid.
    // Without a rotated token the one in the store is spent: clear it so the
    // next call (e.g. a queued Google Pay payment) fails closed instead of
    // sending it again.
    const { pageIdentifier, ...session } = body;
    if (typeof pageIdentifier === "string" && pageIdentifier) {
      virtualTerminalStore.setToken(pageIdentifier);
    } else {
      virtualTerminalStore.setToken("");
      if (!merchantSessionError(session)) {
        console.error(
          "ExpressCheckout Apple Pay session returned no rotated token; aborting"
        );
        throw new Error("Apple Pay merchant validation failed");
      }
    }
    return session;
  }

  // Submits an authorized Apple Pay payment to Payabli and emits the matching
  // express-checkout event. `payment` is the Apple Pay payment object
  // (`{ token, billingContact, shippingContact }`). Returns `{ success }` so the
  // caller can map it to the sheet's completion status. Shared by the in-iframe
  // (Safari) and top-level host (third-party) flows.
  async handleAuthorizedPayment(payment) {
    const randomId = this.payabliConfig.randomId;
    const { ok, data, flow, walletType } = await runWithToken(async () => {
      const result = await processWalletPayment({
        adapter: applePayAdapter,
        config: this.payabliConfig,
        paypoint: this.paypoint,
        urlApi: this.urlApi,
        token: virtualTerminalStore.token,
        event: { payment },
      });
      // Spent either way: store the rotated token, or clear it when the
      // response carries none so a later call fails closed.
      virtualTerminalStore.setToken(result.data?.pageIdentifier || "");
      return result;
    });

    if (isWalletPaymentSuccess({ ok, data })) {
      if (data.responseData && typeof data.responseData === "object") {
        data.responseData.billingContact = payment?.billingContact;
        data.responseData.shippingContact = payment?.shippingContact;
      }
      postExpressCheckoutEvent({
        kind: "success",
        randomId,
        data: { data, paymentMethod: walletType, flow },
      });
      return { success: true };
    }

    // Emit the real API reason as `error.message` (partner contract), not the
    // raw envelope which has no `.message`. The backend sends its error
    // envelope even on a 4xx, so `ok` alone can't be trusted.
    const error = walletErrorFromResult({ data });
    postExpressCheckoutEvent({
      kind: "error",
      randomId,
      data: { error, paymentMethod: walletType, flow },
    });
    console.error(error);
    return { success: false };
  }

  // Emits the partner cancel event. Apple exposes no reason for a cancelled
  // sheet (`oncancel` has no usable error), so — only in the Apple Pay sandbox
  // and only when the user never reached authorization — we ALSO emit an
  // actionable error explaining the most common sandbox failure (real Apple
  // Account / real card in Wallet). Production cancels stay plain so a real
  // user who simply dismisses the sheet never sees a scary message.
  emitApplePayCancel(event, paymentAuthorized) {
    const randomId = this.payabliConfig.randomId;
    const flow = getMode(this.payabliConfig);

    if (!paymentAuthorized && isSandboxApplePay(this.payabliConfig)) {
      postExpressCheckoutEvent({
        kind: "error",
        randomId,
        data: {
          error: new Error(APPLE_PAY_SANDBOX_HINT),
          paymentMethod: applePayAdapter.walletType,
          flow,
        },
      });
    }

    postExpressCheckoutEvent({
      kind: "cancel",
      randomId,
      data: {
        data: event?.sessionError ? { sessionError: event.sessionError } : {},
        paymentMethod: applePayAdapter.walletType,
        flow,
      },
    });
  }

  // Safari path: run the Apple Pay sheet inside this iframe. Safari renders the
  // sheet as an OS surface that presents at the top level, so it escapes the
  // iframe without any host involvement.
  applePayInit(domain) {
    const randomId = this.payabliConfig.randomId;
    try {
      if (!window.ApplePaySession) {
        throw new Error("Apple Pay is not available");
      }

      const session = new window.ApplePaySession(this.apiVersion, this.request);
      // Tracks whether the user actually authorized (Face ID/passcode) before
      // the sheet closed. A cancel WITHOUT authorization is what we enrich with
      // the sandbox hint, since Apple gives no reason for "Payment Not Completed".
      let paymentAuthorized = false;

      session.onvalidatemerchant = async (event) => {
        try {
          const merchantSession = await this.fetchMerchantSession(
            domain,
            event.validationURL
          );
          // Surface a real reason instead of letting completeMerchantValidation
          // fail silently into a reasonless cancel.
          const validationError = merchantSessionError(merchantSession);
          if (validationError) throw validationError;
          session.completeMerchantValidation(merchantSession);
        } catch (err) {
          console.error("ExpressCheckout Apple Pay onvalidatemerchant", err);
          try {
            session.abort();
          } catch (_) {
            // Session may already be torn down.
          }
          postExpressCheckoutEvent({
            kind: "error",
            randomId,
            data: { error: err, paymentMethod: applePayAdapter.walletType },
          });
        }
      };

      session.onpaymentauthorized = async (event) => {
        paymentAuthorized = true;
        try {
          const { success } = await this.handleAuthorizedPayment(event.payment);
          session.completePayment({
            status: success
              ? window.ApplePaySession.STATUS_SUCCESS
              : window.ApplePaySession.STATUS_FAILURE,
          });
        } catch (err) {
          console.error("ExpressCheckout Apple Pay onpaymentauthorized", err);
          postExpressCheckoutEvent({
            kind: "error",
            randomId,
            data: { error: err, paymentMethod: applePayAdapter.walletType },
          });
          try {
            session.completePayment({
              status: window.ApplePaySession.STATUS_FAILURE,
            });
          } catch (_) {
            // Session may already be torn down; don't mask the real error.
          }
        }
      };

      session.oncancel = (event) => {
        this.emitApplePayCancel(event, paymentAuthorized);
      };

      session.begin();
    } catch (e) {
      postExpressCheckoutEvent({
        kind: "error",
        randomId,
        data: { error: e, paymentMethod: applePayAdapter.walletType },
      });
      console.error(e);
    }
  }

  // Third-party path (Chrome/Edge/Firefox): delegate the ApplePaySession to the
  // top-level host (public/component.js) so the SDK draws the QR full-screen on
  // the merchant page instead of inside this iframe. We keep all business logic
  // here and only exchange messages (correlated by randomId) with the host: the
  // host owns the session and drives the sheet/QR; we answer merchant validation
  // and the authorized payment, then tell it whether the charge succeeded.
  // `onSettled` is invoked exactly once when the flow reaches any terminal
  // outcome, letting the caller (ApplePayButton) re-enable itself only once the
  // sheet/QR has actually closed — a third-party QR stays open far longer than
  // any fixed debounce, so this prevents a second click stacking a duplicate
  // session that would cross-talk over the shared randomId channel.
  applePayInitRemote(domain, onSettled = () => {}) {
    const randomId = this.payabliConfig.randomId;
    // Mirror of the in-iframe flow's flag so a cancel without authorization can
    // carry the sandbox hint (see emitApplePayCancel).
    let paymentAuthorized = false;
    // Scope the bridge to the top-level (merchant) origin so the payment
    // credential never crosses to an unexpected frame; "*" disables the check.
    const parentOrigin = resolveParentOrigin();

    const post = (kind, data) =>
      window.parent.postMessage(
        { event: buildApplePayBridgeEvent(kind, randomId), data },
        parentOrigin
      );

    // Symmetric to the host's per-round-trip timeout: guards the initial
    // handshake so a lost `start` (or a merchant running an old component.js
    // without the bridge) surfaces an error instead of a silently dead button.
    let handshakeTimer = null;
    const clearHandshake = () => {
      if (handshakeTimer) clearTimeout(handshakeTimer);
      handshakeTimer = null;
    };
    let settled = false;
    const cleanup = () => {
      clearHandshake();
      window.removeEventListener("message", onMessage);
      if (!settled) {
        settled = true;
        onSettled();
      }
    };

    const onMessage = async (event) => {
      // Reject messages from anything but the top-level parent: combined with the
      // origin check this closes the gap when the origin can't be resolved and
      // falls back to "*" (e.g. Firefox / no-referrer). A real postMessage always
      // sets `source`, so a co-embedded frame can't forge an authorized payment.
      if (event.source && event.source !== window.parent) return;
      if (parentOrigin !== "*" && event.origin !== parentOrigin) return;
      const name = event?.data?.event;
      const payload = event?.data?.data || {};

      if (name === buildApplePayBridgeEvent("ready", randomId)) {
        // The host received `start` and owns the session — liveness confirmed.
        // In the third-party QR flow `validate-merchant` does not fire until the
        // user scans the QR (which can exceed the handshake window), so we clear
        // the handshake on this early ack rather than waiting for validation —
        // otherwise a slow scan looks like a dead/old host and we'd wrongly
        // surface "no response from the host page".
        clearHandshake();
        return;
      }

      if (name === buildApplePayBridgeEvent("validate-merchant", randomId)) {
        // Defense in depth: a new host always acks with `ready` first, but a lost
        // ack still resolves the handshake here (validate-merchant proves liveness).
        clearHandshake();
        try {
          const session = await this.fetchMerchantSession(
            domain,
            payload.validationURL
          );
          // A non-session response (backend/Apple error) must not be handed to
          // the host's completeMerchantValidation — that would only yield a
          // reasonless "Payment Not Completed". Surface the real reason instead.
          const validationError = merchantSessionError(session);
          if (validationError) throw validationError;
          post("merchant-session", { session });
        } catch (err) {
          cleanup();
          // Tell the host to tear down its sheet + listeners so the QR overlay
          // doesn't hang waiting for a session that will never arrive.
          post("abort", {});
          postExpressCheckoutEvent({
            kind: "error",
            randomId,
            data: { error: err, paymentMethod: applePayAdapter.walletType },
          });
        }
      } else if (name === buildApplePayBridgeEvent("authorized", randomId)) {
        paymentAuthorized = true;
        try {
          const { success } = await this.handleAuthorizedPayment(
            payload.payment
          );
          post("complete", { success });
        } catch (err) {
          post("complete", { success: false });
          postExpressCheckoutEvent({
            kind: "error",
            randomId,
            data: { error: err, paymentMethod: applePayAdapter.walletType },
          });
        } finally {
          cleanup();
        }
      } else if (name === buildApplePayBridgeEvent("cancel", randomId)) {
        cleanup();
        this.emitApplePayCancel(payload, paymentAuthorized);
      } else if (name === buildApplePayBridgeEvent("error", randomId)) {
        cleanup();
        postExpressCheckoutEvent({
          kind: "error",
          randomId,
          data: {
            error: payload.error,
            paymentMethod: applePayAdapter.walletType,
          },
        });
      }
    };

    window.addEventListener("message", onMessage);
    post("start", { version: this.apiVersion, request: this.request });

    handshakeTimer = setTimeout(() => {
      handshakeTimer = null;
      cleanup();
      postExpressCheckoutEvent({
        kind: "error",
        randomId,
        data: {
          error: new Error(
            "Apple Pay could not be initialized (no response from the host page)"
          ),
          paymentMethod: applePayAdapter.walletType,
          flow: getMode(this.payabliConfig),
        },
      });
    }, APPLE_PAY_HOST_HANDSHAKE_TIMEOUT_MS);
  }
}

// ─── Availability gating ─────────────────────────────────────────────────────

// Resolves whether the current browser+device can complete the requested flow.
// Async because `applePayCapabilities` (the API Apple recommends for both Safari
// and third-party browsers) returns a Promise.
//
// Gating:
//   1. `window.ApplePaySession` must exist (native Safari or SDK-loaded).
//   2. The required API version must be supported — v14 for autopay/tokenization,
//      v3 for one-time.
//   3. Device capability: prefer `applePayCapabilities(merchantIdentifier)` and
//      fall back to `canMakePayments()` (which is a frequent false negative on
//      desktop third-party browsers, where the cards live on the paired iPhone).
export async function canUseApplePay(payabliConfig, merchantIdentifier) {
  if (!window.ApplePaySession) return false;

  // Cross-browser is opt-in: unless the merchant explicitly set
  // `applePay.crossBrowser: true`, Apple Pay is offered only where it runs
  // natively in-iframe — Safari. In third-party browsers (Chrome/Edge/Firefox)
  // we hide the button so we never start a top-level session the host isn't
  // servicing (the host only injects the SDK when crossBrowser is true).
  if (
    payabliConfig?.expressCheckout?.applePay?.crossBrowser !== true &&
    isThirdPartyApplePay()
  ) {
    return false;
  }

  const requiredVersion = resolveApiVersion(payabliConfig);
  if (
    typeof window.ApplePaySession.supportsVersion === "function" &&
    !window.ApplePaySession.supportsVersion(requiredVersion)
  ) {
    return false;
  }

  if (
    typeof window.ApplePaySession.applePayCapabilities === "function" &&
    merchantIdentifier
  ) {
    try {
      const capabilities = await window.ApplePaySession.applePayCapabilities(
        merchantIdentifier
      );
      if (capabilities?.paymentCredentialStatus !== "applePayUnsupported") {
        return true;
      }
      // `applePayUnsupported` is a frequent false negative in sandbox / not-yet
      // fully-verified merchant setups; fall back to the basic device check
      // rather than hard-hiding the button.
    } catch (_) {
      // Fall through to canMakePayments.
    }
  }

  return (
    typeof window.ApplePaySession.canMakePayments === "function" &&
    window.ApplePaySession.canMakePayments()
  );
}

// Returns an Error describing an *actionable* Apple Pay version mismatch so the
// caller can surface it through the partner's error callback. We only signal
// when the browser actually has Apple Pay (`window.ApplePaySession` exists) but
// is too old for the API version the configured flow needs — v14 for autopay/
// tokenization, v3 for one-time. "Browser has no Apple Pay at all" is not a
// version error and is handled via the usual silent fallback.
export function getApplePayVersionError(payabliConfig) {
  if (!window.ApplePaySession) return undefined;

  const mode = getMode(payabliConfig);
  const needsV14 = mode === "autopay" || mode === "tokenization";
  const requiredVersion = needsV14
    ? APPLE_PAY_RECURRING_API_VERSION
    : APPLE_PAY_ONE_TIME_API_VERSION;

  if (window.ApplePaySession.supportsVersion(requiredVersion)) return undefined;

  return new Error(
    needsV14
      ? "Apple Pay recurring payments require Safari on macOS 13 / iOS 16 or later"
      : "Apple Pay is not supported in this browser version"
  );
}
